import { useEffect, useState, useRef } from "react"
import HCaptcha from "@hcaptcha/react-hcaptcha"

// styles
import Styles from "./smsCode.module.css"

// icons
import { ReactComponent as EditIcon } from "../../../../assets/icons/imported/edit.svg"
import { ReactComponent as MobileIcon } from "../../../../assets/icons/imported/mobile.svg"

// apis
import { _verify, _reSendCode } from "../../redux/actions"

// redux
import { useDispatch, useSelector } from "react-redux"
import {
  setSnackbarData,
  setSnackbarOpen
} from "../../../snackbar/redux/snackbarSlice"

// utils
import { FormattedMessage } from "react-intl"
import Button from "../../../../components/Button/Button"
import InputCode from "../../../../components/InputCode"

// components

const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-ffffffffffff' // Test key

function SMSCode({ setStep, phoneNumber }) {
  // hooks
  const dispatch = useDispatch()

  // states
  const [remainingTime, setRemainingTime] = useState(180)
  const [verificationCode, setVerificationCode] = useState(null)
  const [recaptchaConfirmed, setRecaptchaConfirmed] = useState(false)
  const [recaptchaResponse, setRecaptchaResponse] = useState("")
  const [loading, setLoading] = useState(false)
  const [pendingAction, setPendingAction] = useState(null) // 'verify' or 'resend'
  const captchaRef = useRef(null)
  const userData = useSelector((state) => state.userData.value)

  // constants
  const waitTime = 180

  const onHCaptchaVerify = (token) => {
    setRecaptchaConfirmed(true)
    setRecaptchaResponse(token)
    if (pendingAction === 'verify') {
      performVerify(token)
    } else if (pendingAction === 'resend') {
      performResend(token)
    }
    setPendingAction(null)
  }

  const onHCaptchaError = (error) => {
    setRecaptchaConfirmed(false)
    setRecaptchaResponse("")
    setLoading(false)
    setPendingAction(null)
  }

  const onHCaptchaExpire = () => {
    setRecaptchaConfirmed(false)
    setRecaptchaResponse("")
    setPendingAction(null)
  }

  const handleVerifyClick = () => {
    if (!verificationCode) return
    setLoading(true)
    setPendingAction('verify')
    if (captchaRef.current) {
      captchaRef.current.execute()
    } else {
      // Fallback if captchaRef is not available, though it should be
      performVerify("")
    }
  }

  const handleResendClick = () => {
    setLoading(true)
    setPendingAction('resend')
    if (captchaRef.current) {
      captchaRef.current.execute()
    } else {
      // Fallback if captchaRef is not available
      performResend("")
    }
  }

  const performVerify = (captchaToken) => {
    _verify(
      {
        phone_number: phoneNumber,
        verification_code: verificationCode,
        captcha_response: captchaToken
      },
      (data) => {
        // console.log(data)
        dispatch(setSnackbarOpen(true))
        dispatch(
          setSnackbarData({
            alertMessage: "Successfully Verified",
            severity: "success",
            //buttonMessage: 'See order',
            callback: () => { },
            //actionType: 'link', // link - close
            //linkTo: '/account/orders/open',
            position: "topRight"
          })
        )
        setStep(2)
        setLoading(false)
      },
      (error) => {
        setLoading(false)
        dispatch(setSnackbarOpen(true))
        dispatch(
          setSnackbarData({
            alertMessage: error.errors[0].length ? (
              <FormattedMessage id={error.errors[0]} />
            ) : (
              "Unknown Error"
            ),
            severity: "error",
            //buttonMessage: 'See order',
            callback: () => { },
            //actionType: 'link', // link - close
            //linkTo: '/account/orders/open',
            position: "topRight"
          })
        )
      }
    )
  }

  const performResend = (captchaToken) => {
    setRemainingTime(waitTime)
    _reSendCode(
      {
        phone_number: phoneNumber,
        captcha_response: captchaToken
      },
      (data) => {
        dispatch(setSnackbarOpen(true))
        dispatch(
          setSnackbarData({
            alertMessage: <FormattedMessage id={data.message} />,
            severity: "success",
            //buttonMessage: 'See order',
            callback: () => { },
            //actionType: 'link', // link - close
            //linkTo: '/account/orders/open',
            position: "topRight"
          })
        )
        setLoading(false)
      },
      () => {
        setLoading(false)
      }
    )
  }

  useEffect(() => {
    let intervalId
    if (remainingTime > 0) {
      intervalId = setInterval(() => {
        setRemainingTime((prevTime) => prevTime - 1)
      }, 1000)
    }
    return () => clearInterval(intervalId)
  }, []) // remainingTime

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData("Text")

    if (pastedData.length === 5) { // assuming the OTP is 5 digits long
      setVerificationCode(pastedData)
    }
  }

  return (
    <div className={Styles.smsContainer}>
      {/* icon */}
      <div className={Styles.smsIconContainer}>
        <MobileIcon width={17} />
      </div>

      <h5>SMS Phone Verification</h5>

      <span className={Styles.smsDescription}>
        Insert the 4-digit code we just sent on your number to proceed with the
        KYC process.
      </span>

      <Button
        text={`+ ${phoneNumber?.length ? phoneNumber : userData?.phones[0]?.number
          }`}
        variant="outlined"
        icon={<EditIcon width={12} />}
        onClick={(e) => setStep(0)}
        iconPosition="right"
        style={{ margin: "20px 0", width: "fit-content" }}
      />

      <InputCode
        length={5}
        onComplete={(e) => {
          setVerificationCode(e)
        }}
        onPaste={handlePaste}  // add onPaste event handler
        inputStyle={{
          backgroundColor: "var(--background-color)"
        }}
      />
      <div className="base-sign-up-form__checkbox">
        <HCaptcha
          id="hcaptcha-sms"
          ref={captchaRef}
          size="invisible"
          sitekey={HCAPTCHA_SITE_KEY}
          onVerify={onHCaptchaVerify}
          onError={onHCaptchaError}
          onExpire={onHCaptchaExpire}
          // Assuming 'theme' is a prop passed to SMSCode or defined elsewhere
          // If not, you might want to remove it or define it.
          theme={'light'}
        />
      </div>
      <Button
        text={
          remainingTime > 0
            ? `Resend code in ${remainingTime} seconds`
            : "Resend Code"
        }
        // disabled={disabled}
        variant="link"
        onClick={handleResendClick}
      />

      <Button text="Confirm" disabled={loading || !verificationCode} isLoading={loading} onClick={handleVerifyClick} />
    </div>
  )
}

export default SMSCode
